// SUMMARY: Navbar app launcher - a Grip button opening the app grid in a dropdown.

'use client'

import { useEffect, useState } from 'react'
import { Grip } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AppGrid } from '@/components/nav/app-grid'

export function AppGridMenu() {
	const t = useTranslations('nav.apps')
	const [open, setOpen] = useState(false)
	const [showCli, setShowCli] = useState(false)
	// Whether the API-access tiles apply is plan-gated and not part of the session. Resolving it on FIRST
	// OPEN rather than on mount keeps it off the page-load path entirely - the menu is opened rarely, and
	// the answer is cached for the rest of the session.
	const [accessChecked, setAccessChecked] = useState(false)

	useEffect(() => {
		if (!open || accessChecked) return
		let cancelled = false
		fetch('/api/nav-access')
			.then((res) => (res.ok ? res.json() : { showCli: false }))
			.then((data: { showCli?: boolean }) => {
				if (cancelled) return
				setShowCli(Boolean(data.showCli))
				setAccessChecked(true)
			})
			.catch(() => {
				/* non-critical - the API-access tiles simply stay hidden */
			})
		return () => {
			cancelled = true
		}
	}, [open, accessChecked])

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon" aria-label={t('trigger')}>
					<Grip className="h-5 w-5" />
				</Button>
			</DropdownMenuTrigger>
			{/* Capped + scrollable: with the admin group this list can exceed the viewport on short windows. */}
			<DropdownMenuContent align="end" className="max-h-[80dvh] w-80 overflow-y-auto">
				<DropdownMenuLabel className="px-3 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
					{t('title')}
				</DropdownMenuLabel>
				<AppGrid variant="menu" showCli={showCli} />
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
